import { AccredibleDialogService } from '@accredible-frontend-v2/new-components/dialog';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { delay, filter, switchMap, take, tap } from 'rxjs/operators';
import { RequestSmsAccessDialogComponent } from '../../application/sms/request-sms-access-dialog/request-sms-access-dialog.component';
import { RequestStatus } from '../../entities/campaign-sms.model';
import {
  DashboardState,
  selectDashboardIsIssuerLoaded,
  selectDepartmentPricingFeatures,
} from '../store';
import { SmsApiService } from './sms.service';

@Injectable({ providedIn: 'root' })
export class SmsConfigurationFacade {
  private readonly _store = inject(Store<DashboardState>);
  private readonly _smsApi = inject(SmsApiService);
  private readonly _dialog = inject(AccredibleDialogService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _isSmsActive$ = new BehaviorSubject<boolean>(false);
  readonly isSmsActive$ = this._isSmsActive$.asObservable();

  private readonly _pricingFeatures = this._store.selectSignal(selectDepartmentPricingFeatures);
  private readonly _isGrowthPlan = computed(
    () => this._pricingFeatures()?.corporation_plan === 'growth',
  );

  readonly salesForceSmsRequestUrl = 'https://help.accredible.com/s/sms-feature-request-form';
  readonly requestStatus = signal<RequestStatus>('initial');
  readonly isSmsEnabled = computed(() => this._isGrowthPlan());
  readonly isSmsEnabledAndActivated = computed(
    () => this.isSmsEnabled() && this.requestStatus() === 'active',
  );

  updateSmsStatus(status: RequestStatus): void {
    this.requestStatus.set(status);
    this._isSmsActive$.next(status === 'active');
  }

  loadSmsConfigurationIfSmsEnabled(): void {
    if (this.requestStatus() !== 'initial') {
      return;
    }

    this._store
      .select(selectDashboardIsIssuerLoaded)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((isIssuerLoaded) => isIssuerLoaded),
        filter(() => this.isSmsEnabled()),
        take(1),
        switchMap(() => this._smsApi.loadSmsConfiguration()),
        tap((configuration) => this.updateSmsStatus(configuration?.status ?? 'initial')),
      )
      .subscribe();
  }

  openSMSRequestDialog(): void {
    this._dialog
      .open<RequestStatus, void, RequestSmsAccessDialogComponent>(RequestSmsAccessDialogComponent)
      .closed.pipe(
        filter((status) => !!status),
        tap((status) => this.updateSmsStatus(status)),
        delay(500),
        tap(() => this.openSalesForcePageInNewTab()),
      )
      .subscribe();
  }

  openSalesForcePageInNewTab(): void {
    window.open(this.salesForceSmsRequestUrl, '_blank');
  }
}
